<!--
	@component
	The full accounting mail timeline section: status sub-headline,
	mail status grid with date pickers for mails 1-4, trigger/send buttons,
	and the mailing list ChipList. Owns all derived computations and helper
	functions that belong to this view slice.
-->
<script lang="ts">
	import { t } from '$i18n/i18n'
	import SecondaryButton from '#lib/atoms/SecondaryButton.svelte'
	import Button from '#lib/atoms/Button.svelte'
	import ChipList from '#lib/organisms/ChipList.svelte'
	import Tooltip from '#lib/atoms/Tooltip.svelte'
	import { DefaultRobotoFontProps } from '#lib/types/fontProps.js'
	import { LotMailNotificationType, TooltipPlacements } from '#lib/types/enums.js'
	import { accountingMailConfigState } from './accountingMailConfigState.svelte'
	import MailTimelineRow from './MailTimelineRow.svelte'

	let {
		today,
		onMailDurationChange,
		onTriggerMailDispatching,
		onSendMail,
		onShowSentMail
	}: {
		today: Date
		onMailDurationChange: (
			mailType: LotMailNotificationType,
			newDuration: number | undefined
		) => void
		onTriggerMailDispatching: () => void
		onSendMail: () => void
		onShowSentMail: (index: number, mailingListStr: string) => void
	} = $props()

	// --- Derived ---
	const labelTriggerMailDispatching = $derived(
		accountingMailConfigState.isMailDispatchingDisabled
			? $t('UI.mail.config.button.restartMailing')
			: $t('UI.mail.config.button.stopMailing')
	)

	// --- Helpers ---
	function getTTipTriggerMailDispatching(): string {
		if (accountingMailConfigState.readOnly)
			return $t('UI.mail.config.disable.button.ttip.message.readOnly')
		if (accountingMailConfigState.allMailsSent)
			return $t('UI.mail.config.disable.button.ttip.message.internalMailsSent')
		if (accountingMailConfigState.actualTargetComparisonFilled)
			return $t('UI.mail.config.disable.button.ttip.message.actFilled')
		if (accountingMailConfigState.allDispatchingMailsSent)
			return $t('UI.mail.config.disable.button.ttip.message.externalMailsSent')
		return ''
	}
</script>

<div class="sub-headline">
	<span>{$t('UI.mail.config.subHeadline')}</span>
	{#if accountingMailConfigState.isMailDispatchingDisabled}
		{#if accountingMailConfigState.actualTargetComparisonFilled}
			<span style="color:#C05858">{$t('UI.mail.config.mailDispatchDate.completed')}</span>
		{:else}
			<span style="color:#C05858">{$t('UI.mail.config.mailDispatchDate.paused')}</span>
		{/if}
	{:else}
		<span class="status-lot-accounting-mails-sent"
			>{accountingMailConfigState.accountingMailConfigData.accountingMailData.filter(
				(mail) => mail.dispatchDateMail
			).length}
			von 5 gesendet</span
		>
	{/if}
</div>
<div class="mail-status-container">
	<div class="row-wrapper">
		<div>{$t('UI.mail.config.date')}</div>
		<div>{$t('UI.mail.config.type')}</div>
		<div>{$t('UI.mail.config.status')}</div>
	</div>
	{#each accountingMailConfigState.accountingMailConfigData.accountingMailData as mailData, i (mailData.mailType)}
		<MailTimelineRow index={i} {mailData} {today} {onMailDurationChange} {onShowSentMail} />
	{/each}
</div>
{#if accountingMailConfigState.disableTriggerStartMailDispatchingButton}
	<div style="align-self: flex-end;">
		<Tooltip msg={getTTipTriggerMailDispatching()} placementTtip={TooltipPlacements.BOTTOM_START}>
			<div class="buttons">
				<SecondaryButton
					label={labelTriggerMailDispatching}
					color={accountingMailConfigState.btnColorTriggerMailDispatching}
					disabled
					on:click={onTriggerMailDispatching}
				/>
				<Button
					variant="primary"
					disabled={accountingMailConfigState.disableSendMailButton}
					onclick={onSendMail}
				>
					{$t('UI.mail.config.button.sendMail')}
				</Button>
			</div>
		</Tooltip>
	</div>
{:else}
	<div class="buttons">
		<SecondaryButton
			label={labelTriggerMailDispatching}
			color={accountingMailConfigState.btnColorTriggerMailDispatching}
			on:click={onTriggerMailDispatching}
		/>
		<Button
			variant="primary"
			disabled={accountingMailConfigState.disableSendMailButton}
			onclick={onSendMail}
		>
			{$t('UI.mail.config.button.sendMail')}
		</Button>
	</div>
{/if}

<div class="dispatch-data">
	<div>
		<span>{$t('UI.mail.config.mailDispatchData')}</span>
		<span></span>
	</div>
	<div>
		{#if accountingMailConfigState.mailingList}
			<!-- TODO check here if readonly behavior is still correct -->
			<ChipList
				bind:list={accountingMailConfigState.mailingList}
				placeholder={$t('UI.mail.config.mailDispatchData.address')}
				chipFontProps={DefaultRobotoFontProps('0.625rem', '0.875rem')}
				readOnly={accountingMailConfigState.readOnly ||
					accountingMailConfigState.actualTargetComparisonFilled}
				bind:readOnlyEntries={accountingMailConfigState.defaultReceiverList}
				materialLabel={$t('UI.mail.config.mailDispatchData.address')}
			/>
		{/if}
	</div>
</div>

<style lang="scss">
	@import '../../../styles/global';

	.mail-status-container {
		margin-top: 1rem;
		display: grid;
		grid-template-columns: 0.5fr 1fr 0.5fr;
		grid-template-rows: 1fr; /* remove for unequal rows */

		@include roboto-font(18px, 400, 0.75rem, black);

		width: 100%;

		& .row-wrapper {
			display: contents;

			&:first-child > div {
				border-bottom: 1px solid $primaryGreen;

				@include roboto-font(18px, 400, 0.875rem, black);
			}
		}
	}

	.sub-headline {
		margin-top: 1.2rem;
		display: flex;
		width: 100%;
		justify-content: space-between;

		& span {
			@include roboto-font(normal, 400, 1.25rem, black);

			align-self: flex-start;
		}

		& span:last-child {
			@include roboto-font(normal, 500, 1rem, $primaryGreen);

			align-self: flex-end;
		}
	}

	.dispatch-data {
		width: 100%;

		@include flex-col($justify: flex-start);

		margin-top: 2rem;

		& div {
			display: flex;
			width: 100%;
			flex-direction: column;

			& span {
				align-self: flex-start;

				@include roboto-font(normal, 400, 1.25rem, black);
			}

			& span:last-child {
				margin-top: 2rem;

				@include roboto-font(normal, 500, 1rem, black);
			}
		}

		& div:last-child {
			margin-top: 3rem;
		}

		& div:nth-child(2) {
			margin-top: 1rem;
		}
	}

	.buttons {
		align-self: flex-end;
		display: flex;
		margin-top: 1rem;
		column-gap: 0.6rem;
	}
</style>
